import { Link, useLocation } from 'react-router-dom';
import { usePermission } from '@/hooks/usePermission';
import { OPERATOR_SCREEN_PERMISSIONS } from '@/features/operators/types';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  label: string;
  path: string;
  anyPermissions?: readonly string[];
  roleOnly?: 'cbs_admin' | 'portal';
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Clients',
    items: [
      { label: 'Clients', path: '/clients', anyPermissions: ['MANAGE_CLIENTS', 'CREATE_CLIENT'] },
      { label: 'Pricing', path: '/pricing', anyPermissions: ['MANAGE_PRICING'] },
      { label: 'Revenue', path: '/revenue', anyPermissions: ['VIEW_REVENUE'] },
    ],
  },
  {
    label: 'Transactions',
    items: [
      { label: 'Monitoring', path: '/monitoring', anyPermissions: ['APPROVE_PAYMENT'] },
      {
        label: 'Compliance Panel',
        path: '/transaction-monitoring',
        anyPermissions: ['COMPLIANCE_VIEW'],
        roleOnly: 'cbs_admin',
      },
      { label: 'Incoming', path: '/incoming', anyPermissions: ['MANAGE_INCOMING'] },
    ],
  },
];

const STANDALONE_ITEMS: NavItem[] = [
  { label: 'Logs', path: '/logs', anyPermissions: ['VIEW_AUDIT'] },
  { label: 'Operators', path: '/admin/operators', anyPermissions: OPERATOR_SCREEN_PERMISSIONS },
];

function isActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === '/') {
    return currentPath === '/';
  }
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

/**
 * Fixed sidebar navigation — 240px wide, matching legacy CBS design.
 * Groups: Dashboard (standalone), Clients (Clients/Pricing/Revenue),
 * Transactions (Monitoring/Incoming), then Logs after a divider.
 *
 * Per-item visibility is gated by `anyPermissions` (ANY of the listed codes
 * grants access) or `roleOnly` (role match). Items without either marker
 * render unconditionally — currently only the top-level Dashboard link.
 */
export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { hasAny, hasRole } = usePermission();

  const canShow = (item: NavItem): boolean => {
    if (item.anyPermissions && !hasAny(item.anyPermissions)) return false;
    if (item.roleOnly && !hasRole(item.roleOnly)) return false;
    return true;
  };

  const linkClasses = (path: string) => {
    const active = isActive(currentPath, path);
    return [
      'block border-l-[3px] px-5 py-[9px] text-[13px] font-medium no-underline transition-all duration-150',
      active
        ? 'border-l-[#6B0F1A] bg-white font-semibold text-[#6B0F1A]'
        : 'border-l-transparent text-[#666] hover:bg-[#f0f0f0] hover:text-[#333]',
    ].join(' ');
  };

  const subLinkClasses = (path: string) => {
    const active = isActive(currentPath, path);
    return [
      'block border-l-[3px] px-5 py-[7px] text-[12px] font-medium no-underline transition-all duration-150',
      active
        ? 'border-l-[#6B0F1A] bg-white font-semibold text-[#6B0F1A]'
        : 'border-l-transparent text-[#666] hover:bg-[#f0f0f0] hover:text-[#333]',
    ].join(' ');
  };

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(canShow),
  })).filter((group) => group.items.length > 0);

  const visibleStandalone = STANDALONE_ITEMS.filter(canShow);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[99] bg-black/30 md:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      <nav
        className={[
          'fixed top-0 bottom-0 left-0 z-[100] flex w-[240px] flex-col overflow-y-auto border-r border-[#e8e8e8] bg-[#fafafa] transition-transform duration-200',
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        ].join(' ')}
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div className="border-b border-[#e8e8e8] px-5 pb-5 pt-6 text-[20px] font-extrabold tracking-[0.5px] text-[#6B0F1A]">
          CBS
        </div>

        {/* Nav items */}
        <div className="flex-1 py-4">
          {/* Dashboard — standalone top item, accessible to every authenticated operator */}
          <Link to="/" className={linkClasses('/')} aria-label="Dashboard">
            Dashboard
          </Link>

          {/* Grouped nav sections */}
          {visibleGroups.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[1.5px] text-[#aaa]">
                {group.label}
              </div>
              <div className="pl-4">
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={subLinkClasses(item.path)}
                    aria-label={item.label}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {/* Divider */}
          {visibleStandalone.length > 0 && <div className="mx-5 my-3 h-px bg-[#e8e8e8]" />}

          {/* Standalone items after divider */}
          {visibleStandalone.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={linkClasses(item.path)}
              aria-label={item.label}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}
